/**
 * CleanroomWorkcellFromSim.tsx
 *
 * Event-Driven Cleanroom Workcell connected to AMHS simulation / streaming events.
 * 
 * Synchronizes:
 * 1. OHT Drop Handoff: leaves FOUP at track port position. Big lifter remains IDLE until event.
 * 2. Big External Lifter: strictly triggered by streaming transfer events (DOWN to Equipment Transfer Pos, UP to track).
 * 3. Internal Equipment Lifter: independently triggered to route FOUP from Transfer Pos to STORAGE or PROCESSING.
 */
import React, { useMemo } from 'react';
import type { LayoutElement } from '../../../../types';
import { useEquipmentPortTransfer } from '../Equipment/useEquipmentFoupTransfer';
import { CleanroomWorkcell3D } from './CleanroomWorkcell3D';

export type CleanroomWorkcellFromSimProps = {
  element: LayoutElement | null;
  id?: string;
  width: number;
  height: number;
  depth: number;
  opacity?: number;
  status?: 'RUNNING' | 'IDLE' | 'ERROR' | 'OFFLINE';
  portFractions?: readonly number[];
  onSelect?: () => void;
};

export const CleanroomWorkcellFromSim: React.FC<CleanroomWorkcellFromSimProps> = ({
  element,
  id,
  width,
  height,
  depth,
  opacity = 1,
  status = 'RUNNING',
  portFractions,
  onSelect,
}) => {
  const equipmentId = id ?? element?.id ?? 'WORKCELL-01';

  const fromSim = useEquipmentPortTransfer({
    element,
    width,
    height,
    depth,
    loadPortCount: 1,
    fabProcessTool: false,
  });

  // Map simulation events to Big Lifter state machine
  const { bigLifterEvent, bigLifterProgress } = useMemo(() => {
    if (!fromSim.isTransferring || fromSim.transferProgress01 == null) {
      return {
        bigLifterEvent: null as 'DOWN' | 'UP' | null,
        bigLifterProgress: undefined,
      };
    }

    const progress = fromSim.transferProgress01;
    const op = fromSim.transferOperation;

    return {
      bigLifterEvent: (op === 'DROP' ? 'DOWN' : 'UP') as 'DOWN' | 'UP',
      bigLifterProgress: progress,
    };
  }, [fromSim.isTransferring, fromSim.transferOperation, fromSim.transferProgress01]);

  const resolvedStatus = fromSim.isTransferring
    ? 'RUNNING'
    : (status ?? (fromSim.equipmentStatus === 'RUNNING' ? 'RUNNING' : 'IDLE'));

  return (
    <group position={[0, 0, 0]}>
      <CleanroomWorkcell3D
        id={equipmentId}
        width={width}
        height={height}
        depth={depth}
        opacity={opacity}
        status={resolvedStatus}
        bigLifterEvent={bigLifterEvent}
        bigLifterProgress01={bigLifterProgress}
        internalLifterEvent={null}
        internalLifterProgress01={undefined}
        storageFoupPresent={false}
        processingFoupPresent={false}
        transferFoupPresent={false}
        trackFoupPresent={fromSim.isTransferring && (fromSim.transferProgress01 ?? 0) < 0.1}
        portFractions={portFractions}
        onSelect={onSelect}
      />
    </group>
  );
};

export default CleanroomWorkcellFromSim;
